import "./PlanScreen.css"
import "./PixCheckoutScreen.css"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchSubscriptionStatus } from "../app/features/subscriptionStatusSlice"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useSnackbar } from "react-simple-snackbar"
import { Skeleton } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import requests from "../api/requests"
import { handleAPIError } from "../api/error"
import { getUser } from "../app/features/userSlice"
import CosmosBackground from "../components/CosmosBackground"
import AddressForm, { EMPTY_ADDRESS, addressFromProfile } from "../components/AddressForm"
import Button from "../components/Button"
import PixQrCode from "../components/PixQrCode"
import { DEFAULT_ERROR_MESSAGE, errorSnackBar } from "../components/snackBarOptions"
import { addDaysToToday, formatDate } from "../utils/dates"

const formatPrice = (amount) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount)


/**
 * /plan/pix?price=<id> — Pix checkout (spec asaas-pix W2): billing address
 * (prefilled from the billing profile, CEP via ViaCEP) → QR of the first
 * charge → success → home. Coming back while unpaid re-posts the checkout,
 * which returns the same QR for the same price (D38). A member eligible for
 * the free trial gets no QR: the trial starts at once (api D82, copy T10).
 */
export default function PixCheckoutScreen() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const priceID = searchParams.get("price")
  const [openErrorSnackbar] = useSnackbar(errorSnackBar)

  const [initialAddress, setInitialAddress] = useState(null)
  const [address, setAddress] = useState(null)
  const [checkout, setCheckout] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  // Bumped on invalid_city: the form asks for a city from the list (D71).
  const [invalidCity, setInvalidCity] = useState(0)
  // Free trial offered now and the plan's amount, for the trial copy; the
  // api decides at checkout (a QR answer means no trial after all).
  const [trialDays, setTrialDays] = useState(0)
  const [amount, setAmount] = useState(null)

  useEffect(() => {
    if (!user) {
      navigate("/")
      return
    }
    // Resumed from /awaiting-payment without a plan: choose it again (the
    // api hands back the same QR for the same plan).
    if (!priceID) {
      navigate("/plan", { replace: true })
      return
    }

    let active = true
    requests
      .getBillingProfile()
      .then((res) => active && setInitialAddress(addressFromProfile(res.data)))
      .catch(() => active && setInitialAddress({ ...EMPTY_ADDRESS }))
    requests
      .getPaymentMethods()
      .then((res) => active && setTrialDays(res?.data?.trial_days > 0 ? res.data.trial_days : 0))
      .catch(() => {})
    requests
      .getPlans()
      .then((res) => {
        const plan = (res?.data ?? []).find((p) => p.priceID === priceID)
        if (active && plan) setAmount(plan.amount)
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [user, priceID, navigate])

  const startCheckout = async (billingAddress) => {
    setSubmitting(true)
    try {
      const res = await requests.createPixCheckout({ price_id: priceID, address: billingAddress })
      setAddress(billingAddress)
      setCheckout(res.data)
      // A trial grants access at once: the content gate reads the status.
      if (res.data?.trial && !res.data?.qr) dispatch(fetchSubscriptionStatus())
    } catch (error) {
      const status = error?.response?.status
      const message = error?.response?.data?.message
      if (status === 409) {
        navigate("/")
      } else if (message === "user_blacklisted") {
        navigate("/blocked")
      } else if (message === "pix_unavailable") {
        openErrorSnackbar("O Pix não está disponível para a sua conta. Escolha o cartão de crédito.")
        navigate("/plan")
      } else if (message === "invalid_city") {
        setInvalidCity((n) => n + 1)
      } else if (message === "invalid_address") {
        openErrorSnackbar("Confira o endereço: CEP com 8 dígitos e UF com 2 letras.")
      } else if (status === 401) {
        handleAPIError(error, { dispatch, navigate, openErrorSnackbar })
      } else {
        openErrorSnackbar(DEFAULT_ERROR_MESSAGE)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handlePaid = useCallback(() => navigate("/"), [navigate])

  const price = typeof amount === "number" ? ` de ${formatPrice(amount)}` : ""
  const trial = checkout?.trial && !checkout?.qr ? checkout.trial : null

  return (
    <div className="planScreen__wrapper">
      <CosmosBackground />
      <div className="planScreen pixCheckout">
        <img className="planScreen__logo" src={process.env.PUBLIC_URL + "/Primário_5.svg"} alt="Cosmo" />
        <button className="planScreen__back" onClick={() => navigate("/plan")}>
          <ArrowBackIcon style={{ fontSize: "1rem" }} /> Voltar
        </button>

        {trial ? (
          <TrialStarted
            trial={trial}
            price={price}
            onWatch={() => navigate("/")}
            onPay={() => navigate("/subscription", { state: { pay: true } })}
          />
        ) : !checkout ? (
          <>
            <h1>{trialDays > 0 ? "Assinar com Pix" : "Pagar com Pix"}</h1>
            <h2 className="planScreen__subtitle">
              Precisamos do seu endereço para emitir a nota fiscal da assinatura.
            </h2>
            {initialAddress ? (
              <>
                <AddressForm
                  initialAddress={initialAddress}
                  onSubmit={startCheckout}
                  isLoading={submitting}
                  submitText={trialDays > 0 ? `Começar ${trialDays} dias grátis` : "Gerar código Pix"}
                  invalidCity={invalidCity}
                />
                {trialDays > 0 && (
                  <p className="pixCheckout__trialNote">
                    Nada é cobrado hoje. A 1ª cobrança{price} vence em {formatDate(addDaysToToday(trialDays))}.
                  </p>
                )}
              </>
            ) : (
              <div>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} height={56} animation="wave" sx={{ borderRadius: "8px" }} />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h1>Escaneie ou copie o código</h1>
            <h2 className="planScreen__subtitle">Seu acesso é liberado assim que o pagamento for confirmado.</h2>
            <PixQrCode
              key={checkout.qr?.expires_at}
              qr={checkout.qr}
              paymentMethod={checkout.payment_method}
              onRegenerate={() => startCheckout(address)}
              regenerating={submitting}
              onPaid={handlePaid}
            />
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The Pix trial started (copy T10): access now, the first charge due when the
 * trial ends. Nothing to poll here.
 */
export function TrialStarted({ trial, price, onWatch, onPay }) {
  const endsAt = formatDate(trial.ends_at)
  return (
    <>
      <h1>Seu teste começou!</h1>
      <h2 className="planScreen__subtitle">
        Você tem acesso a todo o conteúdo até {endsAt}. Para continuar depois disso, pague o Pix{price} até essa
        data — vamos te lembrar 3 dias antes. Se não quiser continuar, cancele em Minha assinatura e nada será
        cobrado.
      </h2>
      <Button text="Começar a assistir" onClick={onWatch} />
      <button type="button" className="pixCheckout__secondary" onClick={onPay}>
        Pagar agora
      </button>
    </>
  )
}
